import { useEffect } from "react"
import {
    FieldValues,
    UseFormReturn,
    useForm,
    FieldErrors,
    DefaultValues,
    Path,
    Mode,
} from "react-hook-form"

interface UseFormGeneratorReturn<
    T extends FieldValues,
> extends UseFormReturn<T> {
    errors: FieldErrors<T>
}

/**
 * Custom hook form all form registration in the create application form.
 * This defines a react-hook-form form and sets the default values. It ensures
 * all default values are set in the form. This is done using a useEffect
 * hook. This is required as the `shouldUnregister` option is set to true.
 *
 * @param initialValues - The initial values for the form
 */
const useFormGenerator = <T extends FieldValues = FieldValues>(
    initialValues: T,
    mode: Mode = "all"
): UseFormGeneratorReturn<T> => {
    const formMethods = useForm<T>({
        defaultValues: initialValues as DefaultValues<T>,
        shouldUnregister: true,
        shouldFocusError: true,
        mode,
    })

    const { setValue } = formMethods

    useEffect(() => {
        if (initialValues) {
            Object.entries(initialValues).forEach(([key, value]) => {
                setValue(key as Path<T>, value)
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return { ...formMethods, errors: formMethods.formState.errors }
}

export { useFormGenerator }
